import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { CreateCampaignForm } from '../../../types/IDiy';
import { Brand, Campaign, ChangeEventType } from '../../../types';
import { DEFAULT_COLOR_THEME } from '../../../utils';
import AddIcon from '@mui/icons-material/Add';
import {
  buildAdName,
  buildBaseNamingConvention,
  setAdNameValidationValue,
} from '../../../utils/helpers/DiyHelpers';
import { humanizeString } from '../../../utils/stringModifier';
import { isLeadAds } from '../../../utils/constants/facebookAds';

interface DiyAdDetailsFormV2Props {
  brand: Brand;
  formValues: CreateCampaignForm;
  setFormValues: Dispatch<SetStateAction<CreateCampaignForm>>;
  campaigns: Campaign[];
  isEdit: boolean;
  isAbTest: boolean;
  adId: string;
  setSelectedCampaign: Dispatch<SetStateAction<Campaign>>;
  register: any;
  errors: any;
  clearErrors: any;
  setValue: any;
}

const DiyAdDetailsFormV2: FC<DiyAdDetailsFormV2Props> = ({
  brand,
  formValues,
  setFormValues,
  campaigns,
  isEdit,
  isAbTest,
  adId,
  setSelectedCampaign,
  register,
  errors,
  clearErrors,
  setValue,
}) => {
  const [isAdNameDefined, setIsAdNameDefined] = useState<boolean>(false);
  const [isCampaignNameDefined, setIsCampaignNameDefined] =
    useState<boolean>(false);
  const [isAdsetNameDefined, setIsAdsetnameDefined] = useState<boolean>(false);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);

  useEffect(() => {
    if (campaigns.length > 0) {
      let tempCampaigns: Campaign[] = [];
      if (formValues?.campaignTemplate?.objective) {
        tempCampaigns = campaigns?.filter(
          (campaign: Campaign) =>
            campaign.objective === formValues?.campaignTemplate?.objective,
        );
      } else {
        tempCampaigns = campaigns;
      }

      setFilteredCampaigns(tempCampaigns);
    }
  }, [formValues?.campaignTemplate?.objective, campaigns]);

  const handleOnResetName = () => {
    let temp: CreateCampaignForm = { ...formValues };
    temp = {
      ...temp,
      campaignId: '',
      campaignTemplate: {
        ...temp?.campaignTemplate,
        baseName: '',
      },
      adSetTemplate: {
        ...temp?.adSetTemplate,
        baseName: '',
      },
      adTemplate: {
        ...temp?.adTemplate,
        baseName: '',
      },
    };

    const campaignBaseName = buildBaseNamingConvention({
      formValues: temp,
      level: 'campaign',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      isEdit,
    });

    const adsetBaseName = buildBaseNamingConvention({
      formValues: temp,
      level: 'adset',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      campaignName: campaignBaseName,
      campaignTag: temp?.campaignTemplate?.tag || '',
      isEdit,
    });

    const adBaseName = buildBaseNamingConvention({
      formValues: temp,
      level: 'ad',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      adSetName: adsetBaseName,
      adSetTag: temp?.adSetTemplate?.tag || '',
      isEdit,
    });

    temp = {
      ...temp,
      limited: false,
      campaignTemplate: {
        ...temp?.campaignTemplate,
        baseName: campaignBaseName,
        budgetLevel: 'adset',
        totalBudget: 0,
        daily_budget: 0,
        futureBudget: 0,
        totalCharge: 0,
      },
      adSetTemplate: {
        ...temp?.adSetTemplate,
        baseName: adsetBaseName,
        totalBudget: 0,
        daily_budget: 0,
        futureBudget: 0,
        totalCharge: 0,
      },
      adTemplate: {
        ...temp?.adTemplate,
        baseName: adBaseName,
      },
    };

    setAdNameValidationValue(temp, setValue);
    setFormValues(temp);
    setSelectedCampaign(null);
  };

  const handleOnSelectCampaign = (campaign: Campaign) => {
    let temp: CreateCampaignForm = { ...formValues };

    temp = {
      ...temp,
      campaignId: campaign.id,
      campaignTemplate: {
        ...temp?.campaignTemplate,
        baseName: campaign?.baseName || campaign?.name,
        tag: '',
      },
    };

    const campaignBaseName = buildBaseNamingConvention({
      formValues: temp,
      level: 'campaign',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
    });

    const adsetBaseName = buildBaseNamingConvention({
      formValues: temp,
      level: 'adset',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      campaignName: campaignBaseName,
      campaignTag: temp?.campaignTemplate?.tag,
    });

    const adBaseName = buildBaseNamingConvention({
      formValues: temp,
      level: 'ad',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      adSetName: adsetBaseName,
      adSetTag: temp?.adSetTemplate?.tag,
    });

    temp = {
      ...temp,
      limited: campaign?.limited || false,
      campaignTemplate: {
        ...temp?.campaignTemplate,
        baseName: campaignBaseName,
        budgetLevel: campaign?.budgetLevel || 'adset',
        totalBudget: 0,
        daily_budget: 0,
        futureBudget: 0,
        totalCharge: 0,
      },
      adSetTemplate: {
        ...temp?.adSetTemplate,
        baseName: adsetBaseName,
        totalBudget: 0,
        daily_budget: 0,
        futureBudget: 0,
        totalCharge: 0,
      },
      adTemplate: {
        ...temp?.adTemplate,
        baseName: adBaseName,
      },
    };

    setAdNameValidationValue(temp, setValue);
    setFormValues(temp);
    setSelectedCampaign(campaign);
  };

  const handleOnChangeTag = (e: ChangeEventType, level: string) => {
    let temp: CreateCampaignForm = { ...formValues };

    const campaignName = buildBaseNamingConvention({
      formValues: temp,
      level: 'campaign',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      isEdit,
      adId: isAbTest ? adId : undefined,
    });

    const adsetName = buildBaseNamingConvention({
      formValues: temp,
      level: 'adset',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      campaignName,
      campaignTag:
        level === 'campaign' ? e.target.value : temp?.campaignTemplate?.tag,
      isEdit,
      adId: isAbTest ? adId : undefined,
    });

    const adName = buildBaseNamingConvention({
      formValues: temp,
      level: 'ad',
      brand,
      campaigns: filteredCampaigns,
      limited: temp?.limited,
      adSetName: adsetName,
      adSetTag: level === 'adset' ? e.target.value : temp?.adSetTemplate?.tag,
      isEdit,
      adId: isAbTest ? adId : undefined,
    });

    temp = {
      ...temp,
      campaignTemplate: {
        ...temp?.campaignTemplate,
        baseName: isCampaignNameDefined
          ? temp?.campaignTemplate?.baseName
          : campaignName,
        tag:
          level === 'campaign' ? e.target.value : temp?.campaignTemplate?.tag,
      },
      adSetTemplate: {
        ...temp?.adSetTemplate,
        baseName:
          isAdsetNameDefined || isCampaignNameDefined
            ? temp?.adSetTemplate?.baseName
            : adsetName,
        tag: level === 'adset' ? e.target.value : temp?.adSetTemplate?.tag,
      },
      adTemplate: {
        ...temp?.adTemplate,
        baseName:
          isAdNameDefined || isAdsetNameDefined || isCampaignNameDefined
            ? temp?.adTemplate?.baseName
            : adName,
        tag: level === 'ad' ? e.target.value : temp?.adTemplate?.tag,
      },
    };

    setAdNameValidationValue(temp, setValue);
    setFormValues(temp);
  };

  const handleOnDefineBaseName = (e: ChangeEventType, level: string) => {
    let temp: CreateCampaignForm = { ...formValues };

    if (level === 'campaign') {
      clearErrors('campaignName');
      setIsCampaignNameDefined(true);
      temp = {
        ...temp,
        campaignTemplate: {
          ...temp?.campaignTemplate,
          baseName: e.target.value,
          tag: '',
        },
      };
    } else if (level === 'adset') {
      clearErrors('adsetName');
      setIsAdsetnameDefined(true);
      temp = {
        ...temp,
        adSetTemplate: {
          ...temp?.adSetTemplate,
          baseName: e.target.value,
          tag: '',
        },
      };
    } else if (level === 'ad') {
      clearErrors('adName');
      setIsAdNameDefined(true);
      temp = {
        ...temp,
        adTemplate: {
          ...temp?.adTemplate,
          baseName: e.target.value,
          tag: '',
        },
      };
    }

    setFormValues(temp);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              {...register('description', { required: true })}
              fullWidth
              required
              size="small"
              value={formValues?.adTemplate?.description}
              name="description"
              label="Description"
              multiline
              onChange={(e: ChangeEventType) => {
                clearErrors('description');
                setFormValues({
                  ...formValues,
                  adTemplate: {
                    ...formValues?.adTemplate,
                    description: e.target.value,
                  },
                });
              }}
              InputLabelProps={{ shrink: true }}
              minRows={4}
              maxRows={100}
              error={!!errors?.description}
              helperText={!!errors?.description && 'Description is required'}
            />
          </Grid>

          {!isEdit && !isAbTest && filteredCampaigns?.length > 0 ? (
            <>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Select a Campaign
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2">
                  Select a campaign where to insert your ad. Clear the selection
                  or Click{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontWeight: 'bold' }}
                  >
                    "Add New Campaign"{' '}
                  </Typography>
                  to create a new campaign.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  id="campaigns"
                  renderOption={(props, option) => {
                    return (
                      <Box
                        key={option.id}
                        component="li"
                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <Box
                          component="div"
                          sx={{ flexDirection: 'column', display: 'flex' }}
                        >
                          <Typography variant="body1">{option.name}</Typography>

                          <Typography variant="caption">{option.id}</Typography>

                          <Typography variant="caption">
                            {`Objective: ${
                              isLeadAds(option.objective) ? 'Leads' : 'Traffic'
                            }`}
                          </Typography>

                          <Typography variant="caption">
                            {`Budget Strategy: ${humanizeString(
                              option.budgetLevel || 'adset',
                            )} Level Budget`}
                          </Typography>

                          <Typography variant="caption">
                            {`Schedule: ${
                              option.limited ? 'Limited' : 'On-going'
                            }`}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  options={filteredCampaigns}
                  value={
                    filteredCampaigns?.find(
                      (campaign) => campaign.id === formValues?.campaignId,
                    ) || null
                  }
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Campaigns"
                      fullWidth
                    />
                  )}
                  onChange={async (e: any, value: any, reason) => {
                    if (reason === 'clear') {
                      handleOnResetName();
                    } else {
                      handleOnSelectCampaign(value);
                    }
                  }}
                  PaperComponent={({ children }) => {
                    return (
                      <Paper>
                        {children}

                        <Box component="div" sx={{ textAlign: 'center' }}>
                          <Button
                            type="button"
                            startIcon={<AddIcon sx={{ fontWeight: 'bold' }} />}
                            onMouseDown={handleOnResetName}
                            variant="text"
                            sx={{
                              color: DEFAULT_COLOR_THEME,
                              fontWeight: 'bold',
                            }}
                          >
                            Add New Campaign
                          </Button>
                        </Box>
                      </Paper>
                    );
                  }}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Change Ad Name Tags
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2">
              This tag will be used as the naming convention for the Facebook
              campaign.
            </Typography>
          </Grid>

          {!isAbTest ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                value={formValues?.campaignTemplate?.tag}
                name="campaignNameTag"
                onChange={(e: ChangeEventType) =>
                  handleOnChangeTag(e, 'campaign')
                }
                InputLabelProps={{ shrink: true }}
                label="Campaign Name Tag"
                helperText={buildAdName(
                  formValues?.campaignTemplate?.baseName,
                  formValues?.campaignTemplate?.tag,
                )}
                disabled={!isEdit && !!formValues?.campaignId}
              />
            </Grid>
          ) : null}

          {!isAbTest ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                value={formValues?.adSetTemplate?.tag}
                name="adSetNameTag"
                onChange={(e: ChangeEventType) => handleOnChangeTag(e, 'adset')}
                InputLabelProps={{ shrink: true }}
                label="Adset Name Tag"
                helperText={buildAdName(
                  formValues?.adSetTemplate?.baseName,
                  formValues?.adSetTemplate?.tag,
                )}
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              value={formValues?.adTemplate?.tag}
              name="adNameTag"
              onChange={(e: ChangeEventType) => handleOnChangeTag(e, 'ad')}
              InputLabelProps={{ shrink: true }}
              label="Ad Name Tag"
              helperText={buildAdName(
                formValues?.adTemplate?.baseName,
                formValues?.adTemplate?.tag,
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Facebook Campaign Configuration
            </Typography>
          </Grid>

          {!isAbTest ? (
            <Grid item xs={12}>
              <TextField
                {...(!isAbTest
                  ? register('campaignName', { required: true })
                  : {})}
                fullWidth
                required
                size="small"
                value={buildAdName(
                  formValues?.campaignTemplate?.baseName,
                  formValues?.campaignTemplate?.tag,
                )}
                name="campaignName"
                onChange={(e: ChangeEventType) =>
                  handleOnDefineBaseName(e, 'campaign')
                }
                InputLabelProps={{ shrink: true }}
                label="Campaign Name"
                disabled={!isEdit && !!formValues?.campaignId}
                error={!!errors?.campaignName}
                helperText={
                  !!errors?.campaignName && 'Campaign name is required'
                }
              />
            </Grid>
          ) : null}

          {!isAbTest ? (
            <Grid item xs={12}>
              <TextField
                {...(!isAbTest
                  ? register('adsetName', { required: true })
                  : {})}
                fullWidth
                required
                size="small"
                value={buildAdName(
                  formValues?.adSetTemplate?.baseName,
                  formValues?.adSetTemplate?.tag,
                )}
                name="adsetName"
                onChange={(e: ChangeEventType) =>
                  handleOnDefineBaseName(e, 'adset')
                }
                InputLabelProps={{ shrink: true }}
                label="Adset Name"
                error={!!errors?.adsetName}
                helperText={!!errors?.adsetName && 'Ad set name is required'}
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <TextField
              {...register('adName', { required: true })}
              fullWidth
              required
              size="small"
              value={buildAdName(
                formValues?.adTemplate?.baseName,
                formValues?.adTemplate?.tag,
              )}
              name="adName"
              onChange={(e: ChangeEventType) => handleOnDefineBaseName(e, 'ad')}
              InputLabelProps={{ shrink: true }}
              label="Ad Name"
              error={!!errors?.adName}
              helperText={!!errors?.adName && 'Ad name is required'}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiyAdDetailsFormV2;
